/**
 * O cancelamento do caso pela equipe (E224.F2, owner 05/11, ADR-0151 §5).
 *
 * `POST /casos/{id}/cancelar` com o motivo; a resposta é o caso já cancelado. O servidor decide:
 * a tela só mostra o que ele recusar (já cancelado, exame assinado, fora da equipe, motivo fora
 * dos limites).
 */
import axios, { type AxiosInstance } from 'axios';

import type { CaseRecordData } from './cases';

/** Os limites do motivo, os mesmos do servidor (migration 0186). */
export const MOTIVO_MINIMO_DO_CANCELAMENTO = 10;
export const MOTIVO_MAXIMO_DO_CANCELAMENTO = 500;

export type RecusaDoCancelamento =
  | 'ja_cancelado'
  | 'assinado'
  | 'sem_permissao'
  | 'motivo_invalido'
  | 'indisponivel';

export class CancelamentoError extends Error {
  constructor(readonly motivo: RecusaDoCancelamento) {
    super(motivo);
    this.name = 'CancelamentoError';
  }
}

function recusaDoCancelamento(err: unknown): CancelamentoError {
  if (!axios.isAxiosError(err)) return new CancelamentoError('indisponivel');
  const status = err.response?.status;
  const detalhe: unknown = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (status === 409 && detalhe === 'CASO_ASSINADO') return new CancelamentoError('assinado');
  if (status === 409) return new CancelamentoError('ja_cancelado');
  if (status === 403) return new CancelamentoError('sem_permissao');
  if (status === 422) return new CancelamentoError('motivo_invalido');
  return new CancelamentoError('indisponivel');
}

/** O motivo vale quando, sem os espaços das pontas, cabe nos limites do servidor. */
export function motivoValido(motivo: string): boolean {
  const n = motivo.trim().length;
  return n >= MOTIVO_MINIMO_DO_CANCELAMENTO && n <= MOTIVO_MAXIMO_DO_CANCELAMENTO;
}

export async function cancelarCaso(
  client: AxiosInstance,
  caseId: string,
  motivo: string,
): Promise<CaseRecordData> {
  try {
    const { data } = await client.post<CaseRecordData>(`/casos/${caseId}/cancelar`, {
      motivo: motivo.trim(),
    });
    return data;
  } catch (err) {
    throw recusaDoCancelamento(err);
  }
}
